import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { trackCTAClick, trackNavigation } from './analytics';

/**
 * Services Landing Page
 * 
 * Optimized for keywords:
 * - general contractor chicago
 * - commercial construction companies chicago
 * - construction companies chicago
 * - commercial contractor chicago
 * - general contractors
 */
function Services() {
  const services = [
    {
      id: 'commercial-construction',
      title: 'Commercial Building Construction',
      shortTitle: 'Commercial Construction',
      description: 'Full-service commercial building construction for office spaces, retail centers, restaurants, and mixed-use developments throughout Chicago and Illinois.',
      features: [
        'Ground-up construction',
        'Design-build services',
        'Pre-construction planning',
        'Value engineering',
        'Building permits & compliance'
      ],
      image: '/office2.png',
      keywords: 'commercial construction chicago, commercial building contractor'
    },
    {
      id: 'office-remodeling',
      title: 'Commercial Office Remodeling',
      shortTitle: 'Office Remodeling',
      description: 'Transform your workspace with professional commercial office remodeling services. We specialize in tenant improvements, office build-outs, and corporate renovations.',
      features: [
        'Tenant improvements',
        'Office build-outs',
        'Space planning & design',
        'ADA compliance upgrades',
        'Sustainable renovations'
      ],
      image: '/office1.jpeg',
      keywords: 'commercial office remodeling, office renovation chicago'
    },
    {
      id: 'industrial-construction',
      title: 'Industrial Warehouse Construction',
      shortTitle: 'Industrial Construction',
      description: 'Expert industrial and warehouse construction for manufacturing facilities, distribution centers, and logistics operations across the Chicagoland area.',
      features: [
        'Warehouse construction',
        'Manufacturing facilities',
        'Distribution centers',
        'Cold storage facilities',
        'Loading dock installation'
      ],
      image: '/fcbghero1.webp',
      keywords: 'industrial construction chicago, warehouse construction'
    },
    {
      id: 'healthcare-construction',
      title: 'Healthcare Facility Construction',
      shortTitle: 'Healthcare Construction',
      description: 'Specialized healthcare construction services for hospitals, medical offices, clinics, and dental practices with strict compliance to healthcare regulations.',
      features: [
        'Hospital construction',
        'Medical office build-outs',
        'Ambulatory care centers',
        'Dental office construction',
        'HIPAA-compliant facilities'
      ],
      image: '/fcbghero1.webp',
      keywords: 'healthcare construction chicago, medical office construction'
    },
    {
      id: 'retail-construction',
      title: 'Retail Store Construction',
      shortTitle: 'Retail Construction',
      description: 'Build your retail presence with our expert store construction services. From boutiques to big-box retailers, we deliver on time and on budget.',
      features: [
        'Retail store build-outs',
        'Restaurant construction',
        'Shopping center renovations',
        'Storefront installation',
        'Quick-service restaurant builds'
      ],
      image: '/fcbghero1.webp',
      keywords: 'retail construction chicago, restaurant construction'
    },
    {
      id: 'project-management',
      title: 'Construction Management Services',
      shortTitle: 'Project Management',
      description: 'End-to-end construction management and general contracting services ensuring your project is completed on time, within budget, and to the highest standards.',
      features: [
        'Project scheduling',
        'Budget management',
        'Subcontractor coordination',
        'Quality control',
        'Progress reporting'
      ],
      image: '/fcbghero1.webp',
      keywords: 'construction management chicago, general contracting services'
    }
  ];

  // FAQ Schema for rich snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What areas does Freedom Contracting Group serve?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Freedom Contracting Group serves Chicago and the entire Chicagoland area, including suburbs like Naperville, Aurora, Schaumburg, Oak Brook, and throughout Illinois.'
        }
      },
      {
        '@type': 'Question',
        name: 'What types of commercial construction projects do you handle?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We handle all types of commercial construction including office buildings, retail stores, restaurants, healthcare facilities, industrial warehouses, and tenant improvements.'
        }
      },
      {
        '@type': 'Question',
        name: 'How long does a typical commercial construction project take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Project timelines vary based on scope. Small tenant improvements may take 4-8 weeks, while large commercial buildings can take 12-24 months. We provide detailed timelines during the proposal phase.'
        }
      },
      {
        '@type': 'Question',
        name: 'Are you licensed and insured?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, Freedom Contracting Group is fully licensed, bonded, and insured in the State of Illinois. We maintain comprehensive liability insurance and workers compensation coverage.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you offer free estimates?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we offer free consultations and estimates for all commercial construction projects. Contact us at (312) 675-6000 or through our website to schedule a consultation.'
        }
      }
    ]
  };

  // Service Schema
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'General Contracting and Commercial Construction',
    provider: {
      '@type': 'GeneralContractor',
      name: 'Freedom Contracting Group',
      url: 'https://freedomcg.net'
    },
    areaServed: {
      '@type': 'City',
      name: 'Chicago',
      containedInPlace: {
        '@type': 'State',
        name: 'Illinois'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Commercial Construction Services',
      itemListElement: services.map((service, index) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.title,
          description: service.description
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <SEO 
        title="Commercial Construction Services Chicago | General Contractor"
        description="Freedom Contracting Group is a licensed general contractor in Chicago offering commercial construction, office remodeling, industrial warehouse construction, and construction management services. Call (312) 675-6000 for a free estimate."
        keywords="general contractor chicago, commercial construction companies chicago, construction companies chicago, commercial contractor chicago, commercial office remodeling, general contractors, construction management services chicago"
        path="/services"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' }
        ]}
        additionalSchema={[faqSchema, serviceSchema]}
      />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Our</span>
                <span className="text-blue-900"> Services</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Commercial Construction & General Contracting in Chicago</p>
            </div>
            <Link
              to="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Chicago's Trusted General Contractor
            </h2>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Freedom Contracting Group provides comprehensive commercial construction and general contracting 
              services throughout Chicago and Illinois. With over 20 years of experience and 1,000+ completed 
              projects, we deliver quality craftsmanship on time and within budget.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:+13126756000"
                className="inline-flex items-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-3 px-6 rounded-xl transition-all"
                onClick={() => trackCTAClick('call_services', 'services_hero')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                Call (312) 675-6000
              </a>
              <Link 
                to="/#contact"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                onClick={() => trackCTAClick('free_estimate', 'services_hero')}
              >
                Get a Free Estimate
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            Commercial Construction Services in Chicago
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From ground-up construction to tenant improvements, we offer complete general contracting 
            and construction management services for businesses throughout the Chicagoland area.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <article 
              key={service.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={`${service.title} - Chicago General Contractor`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-4">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/services/${service.id}`}
                  className="inline-flex items-center gap-2 text-blue-900 hover:text-red-600 font-semibold transition-colors"
                  onClick={() => trackNavigation(service.id, 'services_grid')}
                >
                  Learn More
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Why Choose Freedom Contracting Group?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As one of Chicago's leading commercial construction companies, we bring experience, 
              integrity, and American values to every project.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: '20+ Years Experience',
                description: 'Decades of commercial construction expertise in the Chicago market',
                icon: '🏗️'
              },
              {
                title: '1,000+ Projects',
                description: 'Successfully completed projects across all commercial sectors',
                icon: '✅'
              },
              {
                title: 'Licensed & Insured',
                description: 'Fully licensed, bonded, and insured general contractor in Illinois',
                icon: '📋'
              },
              {
                title: 'On-Time Delivery',
                description: 'Proven track record of meeting deadlines and staying within budget',
                icon: '⏰'
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-50 rounded-xl">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="bg-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Service Areas in Illinois
          </h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <p className="text-gray-700 mb-6 text-center max-w-3xl mx-auto">
              Freedom Contracting Group provides commercial construction and general contracting services 
              throughout the Chicago metropolitan area and beyond, including:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-center">
              {[
                'Chicago', 'Naperville', 'Aurora', 'Schaumburg', 'Oak Brook', 'Evanston',
                'Skokie', 'Des Plaines', 'Arlington Heights', 'Joliet', 'Downers Grove', 'Elgin',
                'Waukegan', 'Oak Park', 'Berwyn', 'Cicero', 'Elmhurst', 'Wheaton'
              ].map((city, index) => (
                <div key={index} className="py-2 px-4 bg-slate-50 rounded-lg text-gray-700 text-sm font-medium">
                  {city}, IL
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqSchema.mainEntity.map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-3">
                  {faq.name}
                </h3>
                <p className="text-gray-700">
                  {faq.acceptedAnswer.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-900 to-red-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Commercial Construction Project?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Contact Chicago's trusted general contractor for a free consultation and estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+13126756000"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              Call (312) 675-6000
            </a>
            <a 
              href="mailto:hello@freedomcg.net"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-4 px-8 rounded-xl transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Services;
